"use client";

/**
 * FAQ Renderer
 * Renders FAQ component — static preview in the canvas, interactive in public view.
 * Public view features: expand/collapse answers, search filtering.
 */

import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { ChevronsDownUp, ChevronsUpDown, Plus, Search, X } from "lucide-react";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import type { FaqComponent } from "../../../_types";
import { useCanvasFieldClick } from "../../../_hooks/useCanvasFieldClick";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { resolveSmartLinkTarget } from "../../../_hooks/useSmartLinkTarget";
import { FormModal } from "../../public/FormModal";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";
import { SanitizedHtml } from "./_shared/SanitizedHtml";
import { SectionContainer } from "./_shared/SectionContainer";
import { PROSE_SECTION_TEXT_VARS } from "./_shared/prose-section-text";

interface FaqRendererProps {
  component: FaqComponent;
}

/**
 * Renders a FAQ section — static preview in builder, interactive in public view
 */
export function FaqRenderer({ component }: FaqRendererProps): React.ReactElement {
  const { title, description, items } = component;
  const { isPublic } = usePublicViewContext();
  const { eventId, primaryColor } = useBuilderContext();
  const fieldClick = useCanvasFieldClick(component.id);

  // States — only used in public view
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  // Kept set after the first form-link click (never cleared back to null) so FormModal
  // stays mounted across opens/closes — closing only flips isFormModalOpen, which lets
  // FetchModal's AnimatePresence play its exit animation instead of vanishing on unmount.
  const [formModalTarget, setFormModalTarget] = useState<{ formId: number; inviteToken: string | null } | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  // Handlers
  const toggleItem = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Opens the FAQ answer's form link in place instead of letting it navigate away
  // (ENG-639) — mirrors SmartButton's form-link branch.
  const handleInternalLinkClick = useCallback(
    (href: string): boolean => {
      const target = resolveSmartLinkTarget(href, eventId);
      // Only form links are handled here; this also excludes "unsafe" (sanitize-html already
      // strips javascript:/data:/vbscript:/file: schemes before this HTML is ever rendered).
      if (target.kind !== "form") return false;
      setFormModalTarget({ formId: target.formId, inviteToken: target.inviteToken });
      setIsFormModalOpen(true);
      return true;
    },
    [eventId]
  );

  // Derived values
  const displayItems = useMemo(() => {
    if (!items) return [];

    // Public view: filter by search query and show all items
    if (isPublic) {
      if (!searchQuery.trim()) return items;
      const query = searchQuery.toLowerCase();
      return items.filter(
        (item) => item.question.toLowerCase().includes(query) || item.answer.toLowerCase().includes(query)
      );
    }

    // Builder: show all items
    return items;
  }, [items, isPublic, searchQuery]);

  const allExpanded = useMemo(
    () => displayItems.length > 0 && displayItems.every((item) => expandedIds.has(item.id)),
    [displayItems, expandedIds]
  );
  const shouldHideExpandButton = isPublic && isSearchFocused;

  const toggleAll = useCallback(() => {
    if (allExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(displayItems.map((item) => item.id)));
    }
  }, [allExpanded, displayItems]);

  return (
    <>
      <SectionContainer
        bg="white"
        background={component.background}
        title={title || ""}
        componentId={component.id}
        titleFieldId="faq-title"
      >
        <InlineTipTapEdit
          content={description || ""}
          className={`prose prose-base prose-gray max-w-none prose-a:text-[var(--lp-accent)] prose-a:underline ${PROSE_SECTION_TEXT_VARS}`}
          componentId={component.id}
          fieldId="faq-description"
          field="description"
          placeholder="Click to add description..."
        />

        <div className="flex flex-col gap-6">
          {/* Search bar + expand-all toggle */}
          <div className="flex items-center gap-2">
            <label htmlFor="faq-search" className="sr-only">
              Search questions
            </label>
            <div className="relative min-w-0 flex-1">
              <FetchInput
                id="faq-search"
                type="text"
                value={isPublic ? searchQuery : ""}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="Search questions..."
                leftIcon={<Search className="h-4 w-4" aria-hidden="true" />}
                rightIcon={
                  isPublic && searchQuery ? (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setSearchQuery("")}
                      className="pointer-events-auto flex touch-manipulation items-center justify-center text-gray-400 transition-colors hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : undefined
                }
                disabled={!isPublic}
                aria-label="Search questions"
              />
            </div>
            {isPublic && displayItems.length > 1 && (
              <div
                className={`overflow-hidden transition-[max-width,opacity,transform] duration-200 ease-out ${
                  shouldHideExpandButton ? "max-w-0 translate-x-2 opacity-0" : "max-w-[9rem] opacity-100"
                }`}
              >
                <FetchButton
                  variant="ghost"
                  color="neutral"
                  size="sm"
                  className={`touch-manipulation whitespace-nowrap transition-opacity ${shouldHideExpandButton ? "pointer-events-none" : ""}`}
                  leftIcon={
                    allExpanded ? (
                      <ChevronsDownUp className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronsUpDown className="h-3.5 w-3.5" />
                    )
                  }
                  onClick={toggleAll}
                >
                  {allExpanded ? "Collapse all" : "Expand all"}
                </FetchButton>
              </div>
            )}
          </div>

          {!items || items.length === 0 ? null : displayItems.length === 0 ? (
            <p className="text-center font-medium text-[color:var(--lp-text,#6b7280)]">
              No questions match your search.
            </p>
          ) : (
            <div className="flex flex-col">
              <div className="divide-y divide-gray-200 border-y border-gray-200">
                {displayItems.map((item) => {
                  const isExpanded = expandedIds.has(item.id);
                  const questionId = `faq-question-${item.id}`;
                  const answerId = `faq-answer-${item.id}`;
                  return (
                    <div key={item.id}>
                      {isPublic ? (
                        <button
                          type="button"
                          id={questionId}
                          onClick={() => toggleItem(item.id)}
                          className="group relative z-10 flex w-full cursor-pointer touch-manipulation items-center justify-between gap-3 py-5 text-left"
                          aria-expanded={isExpanded}
                          aria-controls={answerId}
                        >
                          <span className="pointer-events-none text-base font-medium text-[color:var(--lp-text,#111827)] transition-colors [@media(hover:hover)]:group-hover:text-[var(--lp-accent)] md:text-lg">
                            {item.question || "Question"}
                          </span>
                          <Plus
                            className={`pointer-events-none h-4 w-4 flex-shrink-0 text-[color:var(--lp-text,#6b7280)] transition-transform duration-200 [@media(hover:hover)]:group-hover:text-[var(--lp-accent)] ${isExpanded ? "rotate-45" : ""}`}
                            aria-hidden="true"
                          />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={fieldClick(`faq-item:${item.id}`)}
                          className="group flex w-full items-center justify-between gap-3 py-5 text-left"
                        >
                          <span className="text-base font-medium text-[color:var(--lp-text,#111827)] transition-colors group-hover:text-[var(--lp-accent)] md:text-lg">
                            {item.question || "Question"}
                          </span>
                          <Plus
                            className="h-4 w-4 flex-shrink-0 text-[color:var(--lp-text,#6b7280)] transition-colors group-hover:text-[var(--lp-accent)]"
                            aria-hidden="true"
                          />
                        </button>
                      )}

                      {/* Expandable answer (public view only) */}
                      {isPublic && isExpanded && item.answer && (
                        <div id={answerId} role="region" aria-labelledby={questionId} className="pb-5 pr-7">
                          <SanitizedHtml
                            html={item.answer}
                            className={`prose prose-sm prose-gray max-w-none prose-a:text-[var(--lp-accent)] prose-a:underline md:prose-base ${PROSE_SECTION_TEXT_VARS}`}
                            onInternalLinkClick={handleInternalLinkClick}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SectionContainer>
      {formModalTarget && (
        <FormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
          formId={formModalTarget.formId}
          eventId={eventId}
          primaryColor={primaryColor}
          inviteToken={formModalTarget.inviteToken}
        />
      )}
    </>
  );
}
